import classNames from '@sindresorhus/class-names';
import {useSearchState} from '@store/search';
import {retryPromise} from '@utils/retryPromise';
import dynamic from 'next/dynamic';

import {SearchBlockType} from '@strapi/types/MainLanding';

import {useIsMobile} from '@utils/hooks/useIsMobile';
import {useDynamicPlaceholder} from '@web-landing/hooks/useDynamicPlaceholder';

import {logTmrEventAdapter} from '@web-landing/analytics/helpers/logTmrEvent';
import {emitYmEventAdapter} from '@web-landing/analytics/helpers/ymEvent';

import styles from './PicksSearch.module.scss';

type Props = {
  requestUrl: string | undefined;
  searchBlock: SearchBlockType;
  webGatewayRequestUrl?: string;
  redesignMode?: 'default' | 'colored' | '';
};

const SearchInput = dynamic(
  retryPromise(async () =>
    import(
      /* webpackChunkName: "SearchInput" */ '@shared/HeaderSearch/parts/SearchInput/SearchInput'
    ).then(({SearchInput}) => SearchInput),
  ),
);
const AppsList = dynamic(
  retryPromise(async () =>
    import(
      /* webpackChunkName: "AppsList" */ '@shared/HeaderSearch/parts/AppsList/AppsList'
    ).then(({AppsList}) => AppsList),
  ),
);

export const PicksSearch = ({
  requestUrl,
  searchBlock,
  webGatewayRequestUrl,
  redesignMode,
}: Props) => {
  const {placeholder = 'Поиск приложений и игр', placeholderDynamicTexts} =
    searchBlock;
  const [searchPlacement, setSearchPlacement] = useSearchState();
  const {dynamicPlaceholder} = useDynamicPlaceholder({
    placeholder,
    placeholderDynamicTexts,
  });
  const isMobile = useIsMobile();
  const isSearchOpen = searchPlacement === 'bottom';

  const handleShowSearchModal = () => {
    setSearchPlacement('bottom');
  };

  const handleCloseSearchModal = () => {
    setSearchPlacement(null);
  };

  if (isMobile) {
    return (
      <div className={styles.container}>
        <div onClick={handleShowSearchModal}>
          <SearchInput
            className={classNames(
              styles.input,
              redesignMode !== '' && styles.coloredInput,
            )}
            searchBlock={searchBlock}
            emitYmEvent={emitYmEventAdapter}
            logTmrEvent={logTmrEventAdapter}
          />
        </div>
        {isSearchOpen && (
          <div className={styles.modal}>
            <div className={styles.content}>
              <SearchInput
                isMobile={isMobile}
                searchBlock={searchBlock}
                autoFocus
                emitYmEvent={emitYmEventAdapter}
                logTmrEvent={logTmrEventAdapter}
              />
              <AppsList
                searchBlock={searchBlock}
                requestUrl={requestUrl}
                webGatewayRequestUrl={webGatewayRequestUrl}
                emitYmEvent={emitYmEventAdapter}
                logTmrEvent={logTmrEventAdapter}
                cookieDomain=""
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div onClick={handleShowSearchModal}>
        <SearchInput
          isMobile={isMobile}
          className={classNames(
            styles.input,
            redesignMode !== '' && styles.coloredInput,
          )}
          searchBlock={{
            ...searchBlock,
            placeholder: isSearchOpen
              ? searchBlock.placeholder
              : dynamicPlaceholder,
          }}
          emitYmEvent={emitYmEventAdapter}
          logTmrEvent={logTmrEventAdapter}
        />
      </div>

      {isSearchOpen && (
        <>
          <div className={styles.background} onClick={handleCloseSearchModal} />
          <AppsList
            searchBlock={searchBlock}
            requestUrl={requestUrl}
            webGatewayRequestUrl={webGatewayRequestUrl}
            emitYmEvent={emitYmEventAdapter}
            logTmrEvent={logTmrEventAdapter}
            cookieDomain=""
          />
        </>
      )}
    </div>
  );
};
